import { gql, useLazyQuery } from '@apollo/client'
import { TableContainer } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import useFilter from '@promoboxx/use-filter'
import I18n from 'i18n-js'
import { memo, Fragment, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import ContentFilters from '@src/components/content/ContentFilters'
import Pagination from '@src/lib/Pagination'
import urlParamStore from '@src/lib/urlParamStore'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'
import {
  retailerIdsForCurrentBrandOrCurrentRetailerAccountSelector,
  currentRetailerAccountIdSelector,
} from '@src/stores/app/selectors'

import OrganicCampaignListItem from './OrganicCampaignListItem'

const useStyles = makeStyles()((theme) => ({
  absolute: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    // pointerEvents: 'none',
    zIndex: theme.zIndex.appBar - 1,
  },
  absoluteColor: {
    backgroundColor: theme.palette.background.paper,
    opacity: 0.5,
  },
  progressContainer: {
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    paddingTop: theme.spacing(4),
  },
}))

const query = gql`
  query ReportingOrganicCampaignList(
    $page_size: Int!
    $offset: Int!
    $resourceId: ID!
    $retailerIdsArray: [ID!]!
    $retailer_account_id: ID!
    $resourceType: CampaignResourceType!
    $sort_column: String!
    $sort_direction: String!
  ) {
    getCampaigns(
      resourceType: $resourceType
      resourceId: $resourceId
      params: {
        per_page: $page_size
        offset: $offset
        fields: ["configured_channels", "logo_public_id", "share_summary"]
        filterParams: { share_actions: [facebook, twitter, email, instagram] }
        sort_column: $sort_column
        sort_direction: $sort_direction
      }
    ) {
      data {
        id
        name
        creative_path
        state
        configured_channels
        share_summary {
          facebook_share_count
          twitter_share_count
          email_share_count
          embed_shares_count
          asset_shares_count
          engaged_retailer_count
          instagram_share_count
        }
        start_date
        end_date
        logo_public_id
        brand {
          id
          name
          logo_url
        }
        getStats(params: { retailer_ids: $retailerIdsArray }) {
          stats {
            data {
              shares {
                channel
                actions {
                  post_reaction_like
                  post_reaction_haha
                  post_reaction_angry
                  post_reaction_sad
                  post_reaction_love
                  post_reaction_wow
                  clicks
                  video_views
                  comments
                  photo_views
                  posts
                  rsvps
                  retweets
                  favorites
                  engagements
                  saved
                }
                clicks {
                  clicks
                  clicked
                }
                views {
                  impressions_organic
                  impressions_paid
                  opened
                }
              }
            }
          }
        }
        getShares(params: { retailer_account_id: $retailer_account_id }) {
          data {
            id
            scheduled_for
          }
        }
      }
      paging {
        total_results
      }
    }
  }
`

const OrganicCampaignList = (props) => {
  const [runQuery, { data, called, refetch }] = useLazyQuery(query)
  const retailerIds = useSelector(
    retailerIdsForCurrentBrandOrCurrentRetailerAccountSelector,
  )
  const retailerAccountId = useSelector(currentRetailerAccountIdSelector)

  const params = useParams()

  const { previouslySelectedRetailers, setPreviouslySelectedRetailer } =
    usePreviouslySelectedBrand()

  const filterInfo = useFilter('reporting.organic', {
    shouldForceRunOnMount: true,
    store: urlParamStore,
    defaultFilterInfo: {
      filter: {
        brands: previouslySelectedRetailers.map((x) => x.id),
        retailerAccountId,
      },
      pageSize: 10,
      sort: {
        sort_column: 'campaigns.start_date',
        sort_direction: 'desc',
      },
    },
    onChange(filterInfo) {
      const shouldFilterByAll =
        filterInfo.filter.brands.length === 0 ||
        filterInfo.filter.brands.length > 1

      const retailerIdToFilterBy = filterInfo.filter.brands[0]

      const variables = {
        resourceType: shouldFilterByAll ? 'retailer_accounts' : 'retailers',
        resourceId: shouldFilterByAll
          ? filterInfo.filter.retailerAccountId
          : retailerIdToFilterBy,
        // graphql server literally blows up if numbers aren't strings.
        retailerIdsArray: retailerIds.map(String),
        retailer_account_id: filterInfo.filter.retailerAccountId,
        offset: filterInfo.offset,
        page_size: filterInfo.pageSize,
        sort_column: filterInfo.sort.sort_column,
        sort_direction: filterInfo.sort.sort_direction,
      }
      return (called ? refetch(variables) : runQuery({ variables })).then(
        (response) => {
          return {
            filterInfo: {
              totalResults:
                (response.data &&
                  response.data.getCampaigns &&
                  response.data.getCampaigns.paging.total_results) ||
                0,
            },
          }
        },
      )
    },
  })

  useEffect(() => {
    setPreviouslySelectedRetailer(filterInfo.filterInfo.filter.brands)
  }, [filterInfo.filterInfo.filter.brands, setPreviouslySelectedRetailer])

  useEffect(() => {
    // the `retailerIds` value in the filter is only the default, when it
    // changes the filter doesn't run on its own.
    // since it comes from redux we have to put it in as a side effect.
    // we don't want it to run on mount, cause that causes issues in
    // `useFilter` (it relies on one pass for things to settle).
    // piggy backing off of `called` here. technically any sort of "did the
    // filter run once" logic would work here.
    if (called) {
      filterInfo.updateFilter({ retailerIds, retailerAccountId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailerIds, retailerAccountId])

  const { classes, cx } = useStyles()

  let children
  if (data && data.getCampaigns) {
    children = data.getCampaigns.data.map((campaign) => (
      <OrganicCampaignListItem
        campaign={campaign}
        params={params}
        key={campaign.uuid}
      />
    ))
  }

  return (
    <div>
      {props.filterBarRef &&
        createPortal(
          <ContentFilters
            filterInfo={filterInfo.filterInfo}
            onClear={filterInfo.resetFilter}
            onChange={(key, value) => filterInfo.updateFilter({ [key]: value })}
            hideFilters={['search', 'tags', 'show', 'filterByChannel']}
          />,
          props.filterBarRef,
          'reporting-organic-filter-bar',
        )}
      <div style={{ position: 'relative' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{I18n.t('shared.campaign')}</TableCell>
                <TableCell style={{ width: 240 }}>
                  {I18n.t('shared.channels')}
                </TableCell>
                <TableCell style={{ width: 100 }}>
                  {I18n.t('shared.scheduled')}
                </TableCell>
                <TableCell style={{ width: 100 }}>
                  {I18n.t('shared.status')}
                </TableCell>
                <TableCell style={{ width: 20 }}>
                  {I18n.t('shared.views')}
                </TableCell>
                <TableCell style={{ width: 20 }}>
                  {I18n.t('shared.actions')}
                </TableCell>
                <TableCell style={{ width: 20 }}>
                  {I18n.t('shared.clicks')}
                </TableCell>
                <TableCell style={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>{children}</TableBody>
          </Table>
        </TableContainer>
        {filterInfo.isLoading && (
          <Fragment>
            <div className={cx(classes.absolute, classes.absoluteColor)} />
            <div className={classes.absolute}>
              <div className={classes.progressContainer}>
                <CircularProgress />
              </div>
            </div>
          </Fragment>
        )}
      </div>
      <Pagination
        count={filterInfo.filterInfo.totalResults}
        onChangePage={(_event, page) => {
          filterInfo.setPage(page)
        }}
        page={filterInfo.filterInfo.page}
        rowsPerPage={filterInfo.filterInfo.pageSize}
      />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
    </div>
  )
}

export default memo(OrganicCampaignList)
